import CollapsibleSection from "components/DetailPage/CollapsibleSection";
import LabelledDivRow from "components/LabelledDivRow";
import ArtistModalPicker from "components/Picker/ArtistPicker/ArtistModalPicker";
import { useValidateIsAlwaysValid, useValidateIsNotNull } from "formvalidation/hooks";
import React from "react";
import Attendees from "./Attendees";
import PotentialAttendees from "./PotentialAttendees";

interface Props {
  artist: server.dto.ArtistSmall
  attendees: server.dto.ContactSmallV2[]
  onArtistChange: (artist: server.dto.ArtistSmall) => void
  onAttendeesChange: (attendees: server.dto.ContactSmallV2[]) => void

  /**
     * When true: after fetching the available attendees, we check which attendees are the default and select them regardless of what attendees props are passed in.
     * When false: after fetching the available attendees, we pre-select the attendees passed through props
     *
     * Should be true in 'create' pages where attendees are not known (e.g. show create)
     * Should be false in 'edit' pages where attendees are known (e.g. show edit, show copy)
     */
  autoSelectDefaultAttendees: boolean;

  validatorKey: string;
  isRequired: boolean;

  // This is used to overwrite the validation in the show copy page. It should not be used in any new
  //  places and we should move the show copy page to use validators.
  rowRef?: React.RefObject<HTMLDivElement>;
  hasError?: boolean;
}

const ArtistAttendeePickerSection = (props: Props) => {
  const [potentialAttendees, setPotentialAttendees] = React.useState<server.dto.ContactSmallV2[]>([]);

  const mapToContact = (c: server.dto.ContactWithWorkRelation): server.dto.ContactSmallV2 => (c.contact);

  const onArtistSelected = async (artistCompany: server.dto.ArtistSmall) => {
    props.onAttendeesChange([]);
    setPotentialAttendees([]);
    props.onArtistChange(artistCompany);
    if (!artistCompany) {
      return;
    }

    const relatedContacts = await fetchRelatedContacts(artistCompany.artistId);
    props.onAttendeesChange(relatedContacts.filter(c => c.defaultSelect).map(mapToContact));
    setPotentialAttendees(relatedContacts.map(mapToContact));
  };

  const fetchRelatedContacts = async (artistId: number) => {
    const instruction: server.dto.ListContactByCompanyInstruction = {
      companyId: artistId,
      page: 1,
      perPage: 1000,
      isActive: true
    };

    const relatedContacts = await window.CoreApi.CompanyToContact.ListContactByCompany(instruction);
    return relatedContacts.data.data;
  };

  const onAttendeeAdded = (attendee: server.dto.ContactSmallV2) => {
    props.onAttendeesChange([...props.attendees, attendee]);
  };

  const onAttendeeEdited = (attendee: server.dto.ContactSmallV2) => {
    const idx = props.attendees.findIndex(a => a.contactId === attendee.contactId);
    const newAttendees = Object.assign([], props.attendees, { [idx]: attendee });
    props.onAttendeesChange(newAttendees);
  };

  const onAttendeeRemoved = (attendee: server.dto.ContactSmallV2) => {
    const newAttendees = props.attendees.filter(a => a.contactId !== attendee.contactId);
    props.onAttendeesChange(newAttendees);
  };

  React.useEffect(() => {
    if (props.artist) {
      fetchRelatedContacts(props.artist.artistId).then(res => {
        if (props.autoSelectDefaultAttendees) {
          props.onAttendeesChange(res.filter(c => c.defaultSelect).map(mapToContact));
        }
        setPotentialAttendees(res.map(mapToContact));
      });
    }
  }, []);

  const hasArtist = props.artist != null;

  const validation = props.isRequired
    ? useValidateIsNotNull({ key: props.validatorKey, title: site.currentEnvironment.functionGroups.artist.singular, value: props.artist })
    : useValidateIsAlwaysValid(props.validatorKey, site.currentEnvironment.functionGroups.artist.singular);

  return <CollapsibleSection title={site.currentEnvironment.functionGroups.artist.singular} uniqueRowId="artistSection" collapsible="startOpen">
    <LabelledDivRow label={site.currentEnvironment.functionGroups.artist.singular} rowRef={props.rowRef || validation.ref}>
      <div>
        <ArtistModalPicker
          securityScope="limitByUserAccess"
          canCreateNew={site.permissions.addressBook.canCreate}
          afterCreate="closeSidebar"
          nonInPageEditing={{
            onChange: onArtistSelected,
            value: props.artist,
            required: props.isRequired,
            validatorKey: props.validatorKey,
            legacyHasError: props.hasError ? props.hasError : undefined,
          }}
        />
      </div>

      {hasArtist && <div className="flex-column gap-small">
        <div className="s1_gray">{RESX.Planning.Attendees}</div>

        <div className="flex gap-large">
          <div style={{ flex: "1 0 50%" }}>
            <Attendees
              attendees={props.attendees}
              onAttendeeAdded={onAttendeeAdded}
              onAttendeeEdited={onAttendeeEdited}
              onAttendeeRemoved={onAttendeeRemoved}
              canCreate
            />
          </div>
          <div style={{ flex: "1 0 50%" }}>
            <PotentialAttendees
              selectedAttendees={props.attendees}
              potentialAttendees={potentialAttendees}
              onAttendeeSelected={onAttendeeAdded}
            />
          </div>
        </div>
      </div>}
    </LabelledDivRow>
  </CollapsibleSection>;
};

export default ArtistAttendeePickerSection;